import React, { useEffect, useState, useRef, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { Box, Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter, Button, Text, VStack, Spinner } from '@chakra-ui/react';
import { auth } from './services/firebase';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { SkillLevelProvider } from './contexts/SkillLevelContext';

// ============================================================================
//  CAPACITOR IMPORTS FOR MOBILE APP

// ============================================================================
import { Capacitor } from '@capacitor/core';
import gameApiService from './services/gameApiService';
import { App as CapacitorApp } from '@capacitor/app';
import { Keyboard } from '@capacitor/keyboard';
import { StatusBar, Style } from '@capacitor/status-bar';

// Services
//  REMOVED: tableCleanupService - now handled server-side via Cloud Function

import { setUserOnline, setUserOffline, updateHeartbeat } from './services/presenceService';
import userService from './services/userService';
import { ensureFeaturedTablesExist } from './services/featuredTablesService';
import { initializeIAP } from './services/iapService';

// Pages
import LoginPage from './pages/LoginPage';
import HomePage from './pages/HomePage';
import TableBrowserPage from './pages/TableBrowserPage';
import MockTableTourPage from './pages/MockTableTourPage';
import TournamentsPage from './pages/TournamentsPage';
import WalletPage from './pages/WalletPage';
import ProfilePage from './pages/ProfilePage';
import PlayerStatsPage from './pages/PlayerStatsPage';
import HomeGameLedgersPage from './pages/HomeGameLedgersPage';
import GroupsPage from './pages/GroupsPage';
import GroupDetailPage from './pages/GroupDetailPage';
import PublicProfilePage from './pages/PublicProfilePage';
import MessagesPage from './pages/MessagesPage';
import AdminDashboard from './pages/AdminDashboard';
import AgentDashboard from './pages/AgentDashboard';
import BuyCoinsPage from './pages/BuyCoinsPage';
import DepositPage from './pages/DepositPage';
import GamePage from './pages/GamePage';
import CreateTablePage from './pages/CreateTablePage';
import LegalPage from './pages/LegalPage';
import OnboardingPage from './pages/OnboardingPage';
import WelcomePage from './pages/WelcomePage';
import ReferralsPage from './pages/ReferralsPage';

//  Invite Landing Page - Public page for invite links

import InviteLandingPage from './pages/InviteLandingPage';
import VerifyEmailPage from './pages/VerifyEmailPage';

// Training Mode Pages
import TrainingLobby from './pages/TrainingLobby';
import TournamentTrainingPage from './pages/TournamentTrainingPage';
import TournamentChallengePage from './pages/TournamentChallengePage';
import LiveDrillPage from './pages/LiveDrillPage';
import WhatsNewTourPage from './pages/WhatsNewTourPage';
import GlossaryPage from './pages/GlossaryPage';
import GtoChartsPage from './pages/GtoChartsPage';
import TrainingGamePage from './pages/TrainingGamePage';
import TrainingReportPage from './pages/TrainingReportPage';
import PokerCoachPage from './pages/PokerCoachPage';
import ScenarioTablePage from './pages/ScenarioTablePage';

// Components
import Navigation from './components/ui/Navigation';
import BottomNav from './components/ui/BottomNav';
import ActiveSessionsDock from './components/session/ActiveSessionsDock';
import TournamentStartWatcher from './components/tournament/TournamentStartWatcher';
import InviteNotification from './components/InviteNotification';
import GtoQuickButton from './components/training/GtoQuickButton';
import AdminRoute from './components/ui/AdminRoute';
import AgentRoute from './components/ui/AgentRoute';

// Tutorial imports
import TutorialOverlay from './components/tutorial/TutorialOverlay';
import { tutorialService, TutorialType } from './services/tutorialService';

//  Announcement Banner - Shows system announcements to ALL users

import AnnouncementBanner from './components/AnnouncementBanner';
import PaymentsHoldBanner from './components/PaymentsHoldBanner';
import { isStoreSafe, hideSlots } from './utils/storeSafe';
import MaintenanceGuard from './components/MaintenanceGuard';

//  Support Bubble - Shows floating bubble when user has unread support messages

import SupportBubble from './components/SupportBubble';

//  Push Notifications

import pushNotificationService from './services/pushNotificationService';
import InAppNotificationListener from './components/InAppNotificationListener';

//  iOS Optimizations - Safe areas, touch targets, input zoom prevention

import './styles/ios-optimizations.css';

// Slot Games Pages (lazy-loaded to avoid increasing initial bundle)
// Store-safe builds (iPhone / App Store) carry no slot games at all. The
// test is written inline against process.env so webpack sees a constant
// condition and never emits the slot page chunks into the store build (an
// imported flag would leave an unreachable chunk inside the .ipa).
// HIDE_SLOTS_IN_STORE in utils/storeSafe.ts is the runtime side of the same
// switch; to ship slots on iPhone again, flip both.
const SlotsLobbyPage = process.env.REACT_APP_STORE_SAFE === 'true' ? null : React.lazy(() => import('./pages/SlotsLobbyPage'));
const SlotGamePage = process.env.REACT_APP_STORE_SAFE === 'true' ? null : React.lazy(() => import('./pages/SlotGamePage'));
const STORE_SAFE = isStoreSafe();
const SLOTS_HIDDEN = hideSlots();
// Table recordings
const MyRecordingsPage = React.lazy(() => import('./pages/MyRecordingsPage'));
const RecorderViewPage = React.lazy(() => import('./pages/RecorderViewPage'));
const PrizeEligibilityPage = React.lazy(() => import('./pages/PrizeEligibilityPage'));

// ============================================================================
// PROTECTED ROUTE - Requires authentication
// ============================================================================
const ProtectedRoute = ({ children }: { children: React.ReactNode }) => {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((user) => {
      setIsAuthenticated(!!user);
      setLoading(false);
    });
    return unsubscribe;
  }, []);

  if (loading) {
    return (
      <Box
        w="100%"
        h="100vh"
        display="flex"
        alignItems="center"
        justifyContent="center"
        bg="#0D1B2A"
      >
        <Box fontSize="lg" color="#00D9D9">
          Loading...
        </Box>
      </Box>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
};

// ============================================================================
//  ONBOARDING GUARD - Ensures NEW users complete onboarding before accessing app

// ============================================================================
// Checks once on mount, caches result. Existing users are grandfathered in.
//  FIX: More robust check - existing users should NEVER be redirected

// ============================================================================
const OnboardingGuard = ({ children }: { children: React.ReactNode }) => {
  const location = useLocation();
  const { userProfile, loading: authLoading } = useAuth();
  
  // Routes that don't require onboarding completion
  const allowedPaths = ['/onboarding', '/welcome', '/login', '/legal', '/verify-email'];
  const isAllowedPath = allowedPaths.some(path => location.pathname.startsWith(path));

  // Always allow these paths without checking onboarding
  if (isAllowedPath) {
    return <>{children}</>;
  }

  // Wait for auth to finish loading
  if (authLoading) {
    return (
      <Box
        w="100%"
        h="100vh"
        display="flex"
        alignItems="center"
        justifyContent="center"
        bg="#0D1B2A"
      >
        <Box fontSize="lg" color="#00D9D9">
          Loading...
        </Box>
      </Box>
    );
  }

  // No user profile yet - let ProtectedRoute handle auth redirect
  if (!userProfile) {
    return <>{children}</>;
  }

  const profile = userProfile as any;
  
  //  FIX: If profile has a UID, they're an existing authenticated user

  // Wait for profile to fully load from Firestore before making any decisions
  if (!profile.uid && !profile.createdAt && !profile.email && !profile.username) {
    return (
      <Box
        w="100%"
        h="100vh"
        display="flex"
        alignItems="center"
        justifyContent="center"
        bg="#0D1B2A"
      >
        <Box fontSize="lg" color="#00D9D9">
          Loading profile...
        </Box>
      </Box>
    );
  }

  //  FIX: More robust onboarding check - MULTIPLE fallbacks to prevent false redirects

  // ANY of these conditions means they're an existing user who should NOT see onboarding:
  const hasCompletedOnboarding = 
    profile.onboardingCompleted === true ||           // 1. Explicit flag (users who went through onboarding)
    !!profile.username ||                              // 2. Has username = existing user
    !!profile.termsAcceptedAt ||                       // 3. Has accepted terms = existing user
    !!(profile.handsPlayed && profile.handsPlayed > 0) || // 4. Has played hands = active user
    !!(profile.handsWon && profile.handsWon > 0) ||    // 5. Has won hands = active user
    !!(profile.playCoins && profile.playCoins > 0) ||  // 6. Has PlayCoins = existing user
    !!(profile.pkCash && profile.pkCash > 0) ||        // 7. Has PSS Coins = existing user
    !!profile.createdAt;                               // 8. Has createdAt = profile exists in DB

  // Redirect to onboarding ONLY if none of the above conditions are met
  if (!hasCompletedOnboarding) {
    console.log('🔒 [ONBOARDING_GUARD] Redirecting to onboarding (truly new user):', {
      onboardingCompleted: profile.onboardingCompleted,
      username: profile.username,
      termsAcceptedAt: profile.termsAcceptedAt,
      handsPlayed: profile.handsPlayed,
      handsWon: profile.handsWon,
      playCoins: profile.playCoins,
      pkCash: profile.pkCash,
      createdAt: profile.createdAt,
      email: profile.email,
    });
    return <Navigate to="/onboarding" replace />;
  }

  return <>{children}</>;
};

// ============================================================================
// APP LAYOUT - Main layout wrapper
// ============================================================================
const AppLayout = ({ isAuthenticated, currentUserId }: { isAuthenticated: boolean | null; currentUserId: string | null }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const isLoginPage = location.pathname === '/login';
  const isOnboardingPage = location.pathname === '/onboarding' || location.pathname === '/welcome';
  
  // Hide nav on game pages for better experience
  const isGamePage = location.pathname.startsWith('/game/') || location.pathname.startsWith('/training/') && location.pathname !== '/training' || location.pathname.startsWith('/slots/');

  // Tutorial state and logic (app-level to persist across navigation)
  const [searchParams, setSearchParams] = useSearchParams();
  const [showTutorial, setShowTutorial] = useState(false);
  const [tutorialType, setTutorialType] = useState<TutorialType>('main');
  
  //  NEW: Tutorial prompt modal for new users (instead of auto-start)

  const [showTutorialPrompt, setShowTutorialPrompt] = useState(false);
  const tutorialPromptShownRef = useRef(false);

  // Check for tutorial triggers
  useEffect(() => {
    const checkTutorialTrigger = async () => {
      if (!currentUserId || !isAuthenticated) return;
      
      const startTutorialParam = searchParams.get('startTutorial');
      const tutorialTypeParam = searchParams.get('tutorialType') as TutorialType | null;
      
      //  Manual trigger from profile page or URL param - KEEP THIS

      if (startTutorialParam === 'true') {
        setSearchParams({});
        if (tutorialTypeParam === 'agent' || location.pathname === '/agent') {
          setTutorialType('agent');
        } else {
          setTutorialType('main');
        }
        //  FIX: Longer delay (2 seconds) to let page fully stabilize after navigation

        // This prevents the render loop that was happening on PC
        setTimeout(() => {
          requestAnimationFrame(() => {
            setShowTutorial(true);
          });
        }, 2000);
        return;
      }
      
      // New-user tour prompt, one-shot. The version that caused the PC
      // render loop set its guard ref only AFTER two awaits - this effect
      // re-fired during those awaits (searchParams/location deps churn on
      // navigation) and every in-flight run passed the check, each one
      // scheduling another prompt+render. The guard is now claimed
      // SYNCHRONOUSLY before any async work, so re-entry finds it closed.
      if (location.pathname === '/' && !tutorialPromptShownRef.current) {
        tutorialPromptShownRef.current = true;
        const user = auth.currentUser;
        const creationTime = user?.metadata?.creationTime;
        // 24h window, not 10 minutes: a player who signed up on the phone
        // at night and opens the app next morning is still new.
        const isNewUser = creationTime
          ? (Date.now() - new Date(creationTime).getTime()) < 24 * 60 * 60 * 1000
          : false;
        if (isNewUser) {
          const completed = await tutorialService.hasCompletedTutorial(currentUserId, 'main');
          if (!completed) {
            setTimeout(() => setShowTutorialPrompt(true), 1500);
          }
        }
      }
    };
    checkTutorialTrigger();
  }, [currentUserId, isAuthenticated, searchParams, setSearchParams, location.pathname]);

  const handleTutorialComplete = async () => {
    if (currentUserId) {
      await tutorialService.completeTutorial(currentUserId, tutorialType);
    }
    setShowTutorial(false);
  };

  const handleTutorialSkip = async () => {
    if (currentUserId) {
      await tutorialService.skipTutorial(currentUserId, tutorialType);
    }
    setShowTutorial(false);
  };

  // ============================================================================
  //  ANDROID BACK BUTTON HANDLER

  // ============================================================================
  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;

    const backButtonHandler = CapacitorApp.addListener('backButton', ({ canGoBack }) => {
      // If on login page or home, minimize the app
      if (location.pathname === '/login' || location.pathname === '/') {
        CapacitorApp.minimizeApp();
      } else if (canGoBack) {
        // Go back in browser history
        window.history.back();
      } else {
        // Fallback to home
        navigate('/');
      }
    });

    return () => {
      backButtonHandler.remove();
    };
  }, [location.pathname, navigate]);

  // Check if running on native platform for safe area
  const isNative = Capacitor.isNativePlatform();

  return (
    <Box 
      minH="100vh" 
      bg="#0D1B2A"
      //  Apply safe area padding on native platforms

      // Single source of truth for the top inset - the injected
      // .safe-area-top class set the same property and html carried its own
      // padding, stacking up to a double-height dead zone above every page.
      pt={isNative ? 'env(safe-area-inset-top, 0px)' : 0}
    >
      {/*  ANNOUNCEMENT BANNER - Shows to ALL users (authenticated and non-authenticated) */}

      <AnnouncementBanner />
      {/* Persistent payments / KYC hold notice (Admin > Settings). Not dismissable. */}
      {!STORE_SAFE && <PaymentsHoldBanner />}

      {isAuthenticated && !isLoginPage && !isOnboardingPage && !isGamePage && <Navigation />}

      {/* Invite Notifications - show toast for incoming table invites */}
      {isAuthenticated && currentUserId && !isLoginPage && !isOnboardingPage && (
        <InviteNotification currentUserId={currentUserId} />
      )}

      {/* Floating preflop chart button on every training screen */}
      {isAuthenticated && currentUserId && !isLoginPage && !isOnboardingPage && (
        <GtoQuickButton />
      )}

      <Box ml={{ base: 0, md: isLoginPage || isOnboardingPage || isGamePage ? 0 : '80px' }} pb={{ base: isLoginPage || isOnboardingPage || isGamePage ? 0 : '80px', md: 0 }}>
        {/*  ONBOARDING GUARD - Wraps all routes to prevent bypass */}

        <OnboardingGuard>
          <Routes>
            {/* Public Routes */}
            <Route path="/login" element={<LoginPage />} />

            {/* Email verification page */}
            <Route path="/verify-email" element={<VerifyEmailPage />} />

            {/* Legal pages - accessible without auth for compliance */}
            <Route path="/legal" element={<LegalPage />} />

            {/*  Invite Landing Page - Public, no auth required */}

            {/* Handles invite link validation and redirects to login/game */}
            <Route path="/join/:inviteToken" element={<InviteLandingPage />} />

            {/* Onboarding flow for new users */}
            <Route
              path="/onboarding"
              element={
                <ProtectedRoute>
                  <OnboardingPage />
                </ProtectedRoute>
              }
            />

            {/* Welcome page - shown after onboarding, offers tutorial */}
            <Route
              path="/welcome"
              element={
                <ProtectedRoute>
                  <WelcomePage />
                </ProtectedRoute>
              }
            />

            {/* Protected Routes */}
            <Route
              path="/"
              element={
                <ProtectedRoute>
                  <HomePage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/tables"
              element={
                <ProtectedRoute>
                  <TableBrowserPage />
                </ProtectedRoute>
              }
            />
            {/* The tour's practice table - static data, no live game. */}
            <Route
              path="/tour/table"
              element={
                <ProtectedRoute>
                  <MockTableTourPage />
                </ProtectedRoute>
              }
            />
            {/* Create Table Route - Must be before /tables/:id to avoid conflicts */}
            <Route
              path="/tables/create"
              element={
                <ProtectedRoute>
                  <CreateTablePage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/tournaments"
              element={
                <ProtectedRoute>
                  <TournamentsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/wallet"
              element={
                <ProtectedRoute>
                  <WalletPage />
                </ProtectedRoute>
              }
            />
            {/* Own Profile Route */}
            <Route
              path="/profile"
              element={
                <ProtectedRoute>
                  <ProfilePage />
                </ProtectedRoute>
              }
            />
            {/* Player Stats - own performance dashboard */}
            <Route
              path="/my-stats"
              element={
                <ProtectedRoute>
                  <PlayerStatsPage />
                </ProtectedRoute>
              }
            />
            {/* Home Game Ledgers - archived results sheets */}
            <Route
              path="/home-ledgers"
              element={
                <ProtectedRoute>
                  <HomeGameLedgersPage />
                </ProtectedRoute>
              }
            />
            {/* 👥 Groups - player crews with game invites and scheduling */}
            <Route
              path="/groups"
              element={
                <ProtectedRoute>
                  <GroupsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/groups/:groupId"
              element={
                <ProtectedRoute>
                  <GroupDetailPage />
                </ProtectedRoute>
              }
            />
            {/* Public Profile Route - View other users */}
            <Route
              path="/profile/:userId"
              element={
                <ProtectedRoute>
                  <PublicProfilePage />
                </ProtectedRoute>
              }
            />
            {/* Referrals Route */}
            <Route
              path="/referrals"
              element={
                <ProtectedRoute>
                  <ReferralsPage />
                </ProtectedRoute>
              }
            />
            {/* Messages Route */}
            <Route
              path="/messages"
              element={
                <ProtectedRoute>
                  <MessagesPage />
                </ProtectedRoute>
              }
            />
            {/* Admin Route - Protected by AdminRoute component */}
            <Route
              path="/admin"
              element={
                <AdminRoute>
                  <AdminDashboard />
                </AdminRoute>
              }
            />
            {/* Prize eligibility (the only verification entry point on iPhone) */}
            <Route
              path="/eligibility"
              element={
                <ProtectedRoute>
                  <React.Suspense fallback={null}><PrizeEligibilityPage /></React.Suspense>
                </ProtectedRoute>
              }
            />
            {/* Table recordings */}
            <Route
              path="/recordings"
              element={
                <ProtectedRoute>
                  <React.Suspense fallback={null}><MyRecordingsPage /></React.Suspense>
                </ProtectedRoute>
              }
            />
            {/* Headless recorder entry (one-time key in the URL) */}
            <Route path="/rec/:id" element={<React.Suspense fallback={null}><RecorderViewPage /></React.Suspense>} />
            {/* Agent Dashboard Route - Protected by AgentRoute component */}
            <Route
              path="/agent"
              element={
                <AgentRoute>
                  <AgentDashboard />
                </AgentRoute>
              }
            />
            <Route
              path="/buy-coins"
              element={
                <ProtectedRoute>
                  <BuyCoinsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/deposit"
              element={
                STORE_SAFE ? <Navigate to="/buy-coins" replace /> : (
                <ProtectedRoute>
                  <DepositPage />
                </ProtectedRoute>
                )
              }
            />
            <Route
              path="/game/:tableId"
              element={
                <ProtectedRoute>
                  <GamePage />
                </ProtectedRoute>
              }
            />

            {/* What's New cinematic tour */}
            <Route
              path="/whats-new"
              element={
                <ProtectedRoute>
                  <WhatsNewTourPage />
                </ProtectedRoute>
              }
            />

            {/* Training Mode Routes */}
            <Route
              path="/training"
              element={
                <ProtectedRoute>
                  <TrainingLobby />
                </ProtectedRoute>
              }
            />
            <Route
              path="/training/tournament"
              element={
                <ProtectedRoute>
                  <TournamentTrainingPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/training/tournament/:challengeId"
              element={
                <ProtectedRoute>
                  <TournamentChallengePage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/training/tournament/:challengeId/live"
              element={
                <ProtectedRoute>
                  <LiveDrillPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/training/glossary"
              element={
                <ProtectedRoute>
                  <GlossaryPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/training/charts"
              element={
                <ProtectedRoute>
                  <GtoChartsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/training/coach"
              element={
                <ProtectedRoute>
                  <PokerCoachPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/training/scenario"
              element={
                <ProtectedRoute>
                  <ScenarioTablePage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/training/:tableId"
              element={
                <ProtectedRoute>
                  <TrainingGamePage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/training/report/:reportId"
              element={
                <ProtectedRoute>
                  <TrainingReportPage />
                </ProtectedRoute>
              }
            />

            {/* Slot Games Routes (lazy-loaded; absent from store-safe builds) */}
            {!SLOTS_HIDDEN && SlotsLobbyPage && SlotGamePage && (
              <>
                <Route
                  path="/slots"
                  element={
                    <ProtectedRoute>
                      <Suspense fallback={<Box w="100%" h="100vh" display="flex" alignItems="center" justifyContent="center"><Spinner size="xl" color="cyan.400" /></Box>}>
                        <SlotsLobbyPage />
                      </Suspense>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/slots/:gameId"
                  element={
                    <ProtectedRoute>
                      <Suspense fallback={<Box w="100%" h="100vh" display="flex" alignItems="center" justifyContent="center"><Spinner size="xl" color="cyan.400" /></Box>}>
                        <SlotGamePage />
                      </Suspense>
                    </ProtectedRoute>
                  }
                />
              </>
            )}

            {/* 404 Fallback */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </OnboardingGuard>
      </Box>

      {isAuthenticated && !isLoginPage && !isOnboardingPage && !isGamePage && <BottomNav />}

      {/* My Tables dock - persistent multi-tabling UX. Hidden on login/
          onboarding pages but shown everywhere else INCLUDING /game/* so
          users spectating one table can hop back to their own seated table
          with one click. Self-hides when the user has no seated tables. */}
      {isAuthenticated && !isLoginPage && !isOnboardingPage && (
        <>
          <ActiveSessionsDock currentUserId={currentUserId} />
          <TournamentStartWatcher />
        </>
      )}

      {/* Tutorial Overlay - app-level to persist across navigation */}
      {/*  FIX: Don't show tutorial on onboarding page - causes reload loop */}

      {showTutorial && !isOnboardingPage && (
        <TutorialOverlay
          isOpen={showTutorial}
          tutorialType={tutorialType}
          onComplete={handleTutorialComplete}
          onSkip={handleTutorialSkip}
        />
      )}
      
      {/*  NEW: Tutorial Prompt Modal for new users */}

      <Modal isOpen={showTutorialPrompt} onClose={() => setShowTutorialPrompt(false)} isCentered>
        <ModalOverlay bg="blackAlpha.700" />
        <ModalContent bg="gray.800" borderColor="cyan.500" borderWidth="1px" mx={4}>
          <ModalHeader color="white" textAlign="center" pb={2}>
            <Text fontSize="2xl"></Text>

            <Text>Welcome to PokerSweepStars!</Text>
          </ModalHeader>
          <ModalBody>
            <VStack spacing={3} textAlign="center">
              <Text color="gray.300">
                Would you like a quick tour of the app?
              </Text>
              <Text color="gray.500" fontSize="sm">
                Learn how to play, earn PSS Coins, and get the most out of your experience.
              </Text>
            </VStack>
          </ModalBody>
          <ModalFooter justifyContent="center" gap={3}>
            <Button
              variant="ghost"
              color="gray.400"
              onClick={() => setShowTutorialPrompt(false)}
            >
              Maybe Later
            </Button>
            <Button
              colorScheme="cyan"
              onClick={() => {
                setShowTutorialPrompt(false);
                setTutorialType('main');
                setTimeout(() => setShowTutorial(true), 300);
              }}
            >
              Start Tutorial
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/*  SUPPORT BUBBLE - Shows only when user has unread support messages */}

      {/* Renders globally for authenticated users, auto-hides when no unread messages */}
      {isAuthenticated && !isLoginPage && !isOnboardingPage && <SupportBubble />}
    </Box>
  );
};

// ============================================================================
// MAIN APP COMPONENT
// ============================================================================
function App() {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean | null>(null);
  const [currentUserId, setCurrentUserId] = useState<string | null>(null);

  // Dismiss the boot loader (index.html #pss-boot: black screen, logo,
  // loading bar) once React has mounted - fade out, then remove.
  useEffect(() => {
    const boot = document.getElementById('pss-boot');
    if (!boot) return;
    boot.classList.add('pss-boot-hide');
    const t = setTimeout(() => boot.remove(), 350);
    return () => clearTimeout(t);
  }, []);

  // ============================================================================
  //  ENTERPRISE iOS/ANDROID NATIVE PLATFORM SETUP

  // ============================================================================
  // Initializes Capacitor plugins for proper native behavior:
  // - StatusBar: Dark style for our dark theme
  // - Keyboard: Accessory bar visible, scroll enabled, height tracking
  // ============================================================================
  useEffect(() => {
    const setupNativePlatform = async () => {
      const platform = Capacitor.getPlatform();
      
      if (platform === 'ios') {
        document.body.classList.add('capacitor-ios');
        document.body.classList.add('dark-status-bar');

        //  iOS Status Bar - Light content for dark backgrounds

        try {
          await StatusBar.setStyle({ style: Style.Dark });
          console.log('[iOS] StatusBar configured: Dark style');
        } catch (e) {
          console.warn('[iOS] StatusBar plugin not available:', e);
        }

        //  iOS In-App Purchase - Initialize StoreKit

        initializeIAP().catch(e => {
          console.warn('[iOS] IAP initialization failed (non-blocking):', e);
        });
        
        //  iOS Keyboard - Show accessory bar, enable scroll, track height

        try {
          await Keyboard.setAccessoryBarVisible({ isVisible: true });
          await Keyboard.setScroll({ isDisabled: false });
          
          // Track keyboard height for proper layout adjustment
          Keyboard.addListener('keyboardWillShow', (info) => {
            document.body.style.setProperty('--keyboard-height', `${info.keyboardHeight}px`);
            document.body.classList.add('keyboard-open');
          });
          
          Keyboard.addListener('keyboardWillHide', () => {
            document.body.style.setProperty('--keyboard-height', '0px');
            document.body.classList.remove('keyboard-open');
          });
          
          console.log('[iOS] Keyboard configured: Accessory bar visible, scroll enabled');
        } catch (e) {
          console.warn('[iOS] Keyboard plugin not available:', e);
        }
        
      } else if (platform === 'android') {
        document.body.classList.add('capacitor-android');
        
        //  Android Status Bar - Dark style with matching background color

        try {
          await StatusBar.setStyle({ style: Style.Dark });
          await StatusBar.setBackgroundColor({ color: '#0D1B2A' });
          console.log('[Android] StatusBar configured: Dark style, #0D1B2A background');
        } catch (e) {
          console.warn('[Android] StatusBar plugin not available:', e);
        }
      }
    };
    
    setupNativePlatform();
    
    // Cleanup on unmount
    return () => {
      document.body.classList.remove('capacitor-ios', 'capacitor-android', 'dark-status-bar', 'keyboard-open');
      document.body.style.removeProperty('--keyboard-height');
      
      // Remove keyboard listeners on iOS
      if (Capacitor.getPlatform() === 'ios') {
        try {
          Keyboard.removeAllListeners();
        } catch (e) {
          // Ignore cleanup errors
        }
      }
    };
  }, []);

  //  FIX: DISABLED - Table cleanup causes permission errors and CPU loops

  // This should be a Cloud Function, not client-side
  // useEffect(() => {
  //   console.log('[App] Starting table cleanup service...');
  //   tableCleanupService.startAutoCleanup();
  //
  //   return () => {
  //     console.log('[App] Stopping table cleanup service...');
  //     tableCleanupService.stopAutoCleanup();
  //   };
  // }, []);

  // Auth state listener + Presence tracking + Push Notifications
  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(async (user) => {
      setIsAuthenticated(!!user);
      setCurrentUserId(user?.uid || null);

      if (user) {
        // Set user online for presence tracking
        try {
          const username = await userService.getUsername(user.uid);
          await setUserOnline(user.uid, username || 'Player');
          console.log('[Presence] User online:', user.uid);
        } catch (error) {
          console.error('[Presence] Error setting online:', error);
        }

        //  Report foreground state (native): background-only turn alerts
        //  depend on the server knowing when the app is NOT on screen.
        try {
          if (Capacitor.isNativePlatform()) {
            // Heartbeat, not a one-shot: iOS suspends JS the moment the
            // app backgrounds, so the 'inactive' report frequently never
            // sends. The server treats a MISSING fresh heartbeat as
            // backgrounded - all we must do is beat while on screen.
            let heartbeat: ReturnType<typeof setInterval> | null = null;
            const startBeat = () => {
              gameApiService.reportAppState(true);
              if (heartbeat) clearInterval(heartbeat);
              heartbeat = setInterval(() => gameApiService.reportAppState(true), 20_000);
            };
            const stopBeat = () => {
              if (heartbeat) { clearInterval(heartbeat); heartbeat = null; }
              gameApiService.reportAppState(false); // best effort - may not land
            };
            startBeat();
            const { App: CapApp } = await import('@capacitor/app');
            CapApp.addListener('appStateChange', ({ isActive }) => {
              if (isActive) startBeat(); else stopBeat();
            });
          }
        } catch (error) {
          console.warn('[AppState] reporter failed:', error);
        }

        //  Initialize push notifications for mobile

        try {
          console.log('[Push] Initializing push notification service...');
          await pushNotificationService.initialize();
          console.log('[Push] Push notification service initialized');
        } catch (error) {
          // Non-fatal - push notifications are optional
          console.warn('[Push] Failed to initialize push notifications:', error);
        }

        //  Ensure featured tables exist (for Quick Match to work on first click)

        try {
          console.log('[FeaturedTables] Ensuring tables exist...');
          await ensureFeaturedTablesExist();
          console.log('[FeaturedTables] Tables ready');
        } catch (error) {
          // Non-fatal - scheduled function will create them
          console.warn('[FeaturedTables] Error ensuring tables:', error);
        }
      } else {
        // User logged out - cleanup push notifications
        try {
          await pushNotificationService.cleanup();
        } catch (error) {
          console.warn('[Push] Error cleaning up push notifications:', error);
        }
      }
    });

    return () => {
      unsubscribe();
    };
  }, []);

  // Handle page unload - set user offline
  useEffect(() => {
    const handleBeforeUnload = () => {
      if (currentUserId) {
        // Use sendBeacon for reliable offline status on page close
        setUserOffline(currentUserId);
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      // Cleanup on unmount
      if (currentUserId) {
        setUserOffline(currentUserId);
      }
    };
  }, [currentUserId]);

  // Heartbeat to keep presence fresh (every 30 seconds)
  useEffect(() => {
    if (!currentUserId) return;

    const interval = setInterval(() => {
      updateHeartbeat(currentUserId);
    }, 30000);

    return () => clearInterval(interval);
  }, [currentUserId]);

  //  MOBILE APP LIFECYCLE - Handle app state changes (background/foreground)

  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;

    // Listen for app state changes
    const stateListener = CapacitorApp.addListener('appStateChange', async ({ isActive }) => {
      if (currentUserId) {
        if (isActive) {
          // App came to foreground - set user online
          try {
            const username = await userService.getUsername(currentUserId);
            await setUserOnline(currentUserId, username || 'Player');
            console.log('[Mobile] App resumed, user online');
          } catch (error) {
            console.error('[Mobile] Error setting online on resume:', error);
          }
        } else {
          // App went to background - set user offline
          try {
            await setUserOffline(currentUserId);
            console.log('[Mobile] App backgrounded, user offline');
          } catch (error) {
            console.error('[Mobile] Error setting offline on background:', error);
          }
        }
      }
    });

    // Listen for back button (Android primarily, but good to have)
    const backListener = CapacitorApp.addListener('backButton', ({ canGoBack }) => {
      if (canGoBack) {
        window.history.back();
      } else {
        // At root - minimize app instead of exiting
        CapacitorApp.minimizeApp();
      }
    });

    return () => {
      stateListener.remove();
      backListener.remove();
    };
  }, [currentUserId]);

  return (
    <BrowserRouter>
      {/* Maintenance mode (Admin > Settings) replaces the whole app for
          non-admins with the maintenance message; it was never mounted before. */}
      <MaintenanceGuard>
        <AuthProvider>
          <SkillLevelProvider>
            {/*  In-App Notification Listener - Shows foreground push notifications */}

            <InAppNotificationListener />
            <AppLayout isAuthenticated={isAuthenticated} currentUserId={currentUserId} />
          </SkillLevelProvider>
        </AuthProvider>
      </MaintenanceGuard>
    </BrowserRouter>
  );
}

export default App;